import {
  CalendarPresetIdSchema,
  EncounterBlockerSchema,
  EncounterLifecycleSchema,
  EncounterOwnerSchema,
  PracticeRequestStatusSchema,
  resolveCalendarPresetDateRange,
} from '../../../../core'
import type { CalendarPresetId, SortingState, Updater } from '../../../../core'
import { useApiOrgSlug } from '../../../utils/orgSlug'
import { useApiQuery } from '../../../utils/reactQuery'
import type {
  ArrayFilterOperator,
  OperatorArrayFilter,
} from '../../filtering/utils/queryParams'
import {
  createOperatorArrayDefault,
  parseAsOperatorArrayOf,
} from '../../filtering/utils/queryParams'
import { EncounterResolutionOutcomeSchema } from '../components/EncounterOperationalFacts'
import { BillingTypeSchema } from '../components/details/EncounterBillingTypeBadge'
import { EncounterPaymentUpdateTypeSchema } from '../components/urgency/EncounterUrgencyMetadata'
import {
  AgentRunStatusSchema,
  EncounterEnrichmentStatusSchema,
  EncounterSnoozeFilterSchema,
  GoLiveCohortSchema,
} from '../utils/encounterSchemas'
import type { components, operations } from '@joyful/openapi/types.gen'
import {
  parseAsPlainDate,
  parseAsUuid,
  WorkflowStatusSchema,
  WorkflowStatusSimplifiedSchema,
  createEnumSchema,
  toPlainDate,
} from '@joyful/utils'
import { keepPreviousData } from '@tanstack/react-query'
import type { SetValues, Values } from 'nuqs'
import {
  createSerializer,
  parseAsArrayOf,
  parseAsBoolean,
  parseAsFloat,
  parseAsInteger,
  parseAsString,
  parseAsStringEnum,
  useQueryState,
  useQueryStates,
} from 'nuqs'
import { z } from 'zod'
import type { Temporal } from '@js-temporal/polyfill'

const PAYER_IDENTIFICATION_FILTER_VALUE_PREFIX = 'payer-id:'
const PAYER_NAME_PREFIX_FILTER_VALUE_PREFIX = 'payer-prefix:'
const PROCEDURE_CODE_PREFIX_FILTER_VALUE_PREFIX = 'procedure-prefix:'

export function createPayerIdentificationFilterValue(value: string) {
  return `${PAYER_IDENTIFICATION_FILTER_VALUE_PREFIX}${value}`
}

export function createPayerNamePrefixFilterValue(value: string) {
  return `${PAYER_NAME_PREFIX_FILTER_VALUE_PREFIX}${value}`
}

export function createProcedureCodePrefixFilterValue(value: string) {
  return `${PROCEDURE_CODE_PREFIX_FILTER_VALUE_PREFIX}${value}`
}

export function isPayerIdentificationFilterValue(value: string) {
  return value.startsWith(PAYER_IDENTIFICATION_FILTER_VALUE_PREFIX)
}

export function isPayerNamePrefixFilterValue(value: string) {
  return value.startsWith(PAYER_NAME_PREFIX_FILTER_VALUE_PREFIX)
}

export function isProcedureCodePrefixFilterValue(value: string) {
  return value.startsWith(PROCEDURE_CODE_PREFIX_FILTER_VALUE_PREFIX)
}

export interface LabeledFilterDisplayParts {
  label: string
  value: string
}

export function getPayerFilterDisplayParts(
  value: string,
): LabeledFilterDisplayParts | null {
  if (isPayerIdentificationFilterValue(value)) {
    return {
      label: 'Payer ID',
      value: value.slice(PAYER_IDENTIFICATION_FILTER_VALUE_PREFIX.length),
    }
  }

  if (isPayerNamePrefixFilterValue(value)) {
    return {
      label: 'Payer prefix',
      value: value.slice(PAYER_NAME_PREFIX_FILTER_VALUE_PREFIX.length),
    }
  }

  return null
}

export function getPayerFilterDisplayValue(value: string) {
  const parts = getPayerFilterDisplayParts(value)
  if (parts !== null) {
    return `${parts.label}: ${parts.value}`
  }

  return value
}

export function getProcedureCodeFilterDisplayParts(
  value: string,
): LabeledFilterDisplayParts | null {
  if (isProcedureCodePrefixFilterValue(value)) {
    return {
      label: 'Procedure prefix',
      value: value.slice(PROCEDURE_CODE_PREFIX_FILTER_VALUE_PREFIX.length),
    }
  }

  return null
}

export function getProcedureCodeFilterDisplayValue(value: string) {
  const parts = getProcedureCodeFilterDisplayParts(value)
  if (parts !== null) {
    return `${parts.label}: ${parts.value}`
  }

  return value
}

function appendUniqueValues(values: Array<string>, nextValues: Array<string>) {
  const mergedValues = [...values]
  for (const value of nextValues) {
    if (!mergedValues.includes(value)) {
      mergedValues.push(value)
    }
  }

  return mergedValues
}

function getPayerNameFilterValues(values: Array<string>) {
  return values.filter((value) => {
    return (
      !isPayerIdentificationFilterValue(value) &&
      !isPayerNamePrefixFilterValue(value)
    )
  })
}

function getPayerIdentificationFilterValues(values: Array<string>) {
  return values.filter(isPayerIdentificationFilterValue).map((value) => {
    return value.slice(PAYER_IDENTIFICATION_FILTER_VALUE_PREFIX.length)
  })
}

function getPayerNamePrefixFilterValues(values: Array<string>) {
  return values.filter(isPayerNamePrefixFilterValue).map((value) => {
    return value.slice(PAYER_NAME_PREFIX_FILTER_VALUE_PREFIX.length)
  })
}

function getProcedureCodeFilterValues(values: Array<string>) {
  return values.filter((value) => {
    return !isProcedureCodePrefixFilterValue(value)
  })
}

function getProcedureCodePrefixFilterValues(values: Array<string>) {
  return values.filter(isProcedureCodePrefixFilterValue).map((value) => {
    return value.slice(PROCEDURE_CODE_PREFIX_FILTER_VALUE_PREFIX.length)
  })
}

export const EncountersViewSchema = createEnumSchema<
  components['schemas']['EncounterListView']
>([
  {
    title: 'All',
    value: 'all',
  },
  {
    title: 'Payer Balance',
    value: 'payer',
  },
  {
    title: 'Patient Balance',
    value: 'patient',
  },
  {
    title: 'Assigned to me',
    value: 'assigned',
  },
  {
    title: 'Joyful follow-up',
    value: 'flagged_for_joyful_followup',
  },
])

export const PayerResponseFilterScopeSchema = createEnumSchema<
  components['schemas']['PayerResponseFilterScope']
>([
  {
    title: 'Any payer response',
    value: 'any',
  },
  {
    title: 'Latest payer response',
    value: 'latest',
  },
])

const encountersViewQueryParams = {
  view: parseAsStringEnum(EncountersViewSchema.values()).withDefault('all'),
}
export function useEncountersView() {
  return useQueryState(
    'view',
    encountersViewQueryParams.view.withOptions({ history: 'push' }),
  )
}

export const serializeEncountersView = createSerializer(
  encountersViewQueryParams,
)

export const EncountersListPaginationQueryParams = {
  limit: parseAsInteger.withDefault(50),
  page: parseAsInteger.withDefault(1),
}

export function useEncountersPagination() {
  const [encountersPagination, setEncountersPagination] = useQueryStates(
    EncountersListPaginationQueryParams,
    { history: 'push' },
  )

  return {
    encountersPagination,
    setEncountersPagination,
  }
}

export const EncountersFiltersSortKeySchema = z.enum<
  ReadonlyArray<components['schemas']['EncounterListSortKey']>
>([
  'aging_days',
  'due_date',
  'last_biller_action_at',
  'patient_name',
  'provider_name',
  'service_date',
  'recovered_amount',
  'total_receivable',
  'total_collected',
  'total_outstanding',
  'owner',
])

type TAgingBucket = components['schemas']['AgingBucket']
export const EncounterAgingBucketSchema = createEnumSchema<TAgingBucket>([
  {
    title: '< 30 days',
    value: 'LESS_THAN_30_DAYS',
  },
  {
    title: '30-60 days',
    value: 'BETWEEN_30_60_DAYS',
  },
  {
    title: '60-75 days',
    value: 'BETWEEN_60_75_DAYS',
  },
  {
    title: '75-90 days',
    value: 'BETWEEN_75_90_DAYS',
  },
  {
    title: '90-120 days',
    value: 'BETWEEN_90_120_DAYS',
  },
  {
    title: '> 120 days',
    value: 'GREATER_THAN_120_DAYS',
  },
])

export const EncountersFiltersQueryParams = {
  adjustmentCodesFilter: parseAsOperatorArrayOf(parseAsString).withDefault(
    createOperatorArrayDefault(),
  ),
  adjustmentCodesFilterScope: parseAsStringEnum(
    PayerResponseFilterScopeSchema.values(),
  ).withDefault('any'),
  agentRunStatusesFilter: parseAsArrayOf(
    parseAsStringEnum(AgentRunStatusSchema.values()),
  ).withDefault([]),
  agingBucketsFilter: parseAsArrayOf(
    parseAsStringEnum(EncounterAgingBucketSchema.values()),
  ).withDefault([]),
  assignedBillerUsersFilter: parseAsArrayOf(parseAsString).withDefault([]),
  billingProviderNpisFilter: parseAsOperatorArrayOf(parseAsString).withDefault(
    createOperatorArrayDefault(),
  ),
  billingProviderTaxIdsFilter: parseAsOperatorArrayOf(
    parseAsString,
  ).withDefault(createOperatorArrayDefault()),
  billingTypeFilter: parseAsStringEnum(BillingTypeSchema.values()),
  blockersFilter: parseAsArrayOf(
    parseAsStringEnum(EncounterBlockerSchema.values()),
  ).withDefault([]),
  closeActionDateEndFilter: parseAsPlainDate,
  closeActionDatePresetFilter: parseAsStringEnum(
    CalendarPresetIdSchema.options,
  ),
  closeActionDateStartFilter: parseAsPlainDate,
  dateEndFilter: parseAsPlainDate,
  datePresetFilter: parseAsStringEnum(CalendarPresetIdSchema.options),
  dateStartFilter: parseAsPlainDate,
  deniedProcedureCodesExactlyFilter: parseAsArrayOf(parseAsString).withDefault(
    [],
  ),
  deniedProcedureCodesFilter: parseAsOperatorArrayOf(parseAsString).withDefault(
    createOperatorArrayDefault(),
  ),
  drilldownFilter: parseAsUuid,
  externalClaimIdsFilter: parseAsArrayOf(parseAsString).withDefault([]),
  flaggedDateEndFilter: parseAsPlainDate,
  flaggedDatePresetFilter: parseAsStringEnum(CalendarPresetIdSchema.options),
  flaggedDateStartFilter: parseAsPlainDate,
  flaggedForJoyfulFollowupFilter: parseAsBoolean,
  goLiveCohortFilter: parseAsStringEnum(GoLiveCohortSchema.values()),
  hasClaimResponsesFilter: parseAsBoolean,
  hasHistoricalDeniedClaimResponseFilter: parseAsBoolean,
  idsFilter: parseAsArrayOf(parseAsString).withDefault([]),
  insuranceOutstandingMaxFilter: parseAsFloat,
  insuranceOutstandingMinFilter: parseAsFloat,
  lastActionDateEndFilter: parseAsPlainDate,
  lastActionDatePresetFilter: parseAsStringEnum(CalendarPresetIdSchema.options),
  lastActionDateStartFilter: parseAsPlainDate,
  lastAgentRunDateEndFilter: parseAsPlainDate,
  lastAgentRunDatePresetFilter: parseAsStringEnum(
    CalendarPresetIdSchema.options,
  ),
  lastAgentRunDateStartFilter: parseAsPlainDate,
  lastContactDateEndFilter: parseAsPlainDate,
  lastContactDatePresetFilter: parseAsStringEnum(
    CalendarPresetIdSchema.options,
  ),
  lastContactDateStartFilter: parseAsPlainDate,
  lastContactOlderThanDaysFilter: parseAsInteger,
  lastResubmissionAppealDateEndFilter: parseAsPlainDate,
  lastResubmissionAppealDatePresetFilter: parseAsStringEnum(
    CalendarPresetIdSchema.options,
  ),
  lastResubmissionAppealDateStartFilter: parseAsPlainDate,
  latestCandidStatusesFilter: parseAsOperatorArrayOf(parseAsString).withDefault(
    createOperatorArrayDefault(),
  ),
  latestClaimPrimaryPayerIdentificationsFilter: parseAsOperatorArrayOf(
    parseAsString,
  ).withDefault(createOperatorArrayDefault()),
  latestClaimPrimaryPayerNamesFilter: parseAsOperatorArrayOf(
    parseAsString,
  ).withDefault(createOperatorArrayDefault()),
  latestTebraStatusesFilter: parseAsOperatorArrayOf(parseAsString).withDefault(
    createOperatorArrayDefault(),
  ),
  lifecycleOutcomesFilter: parseAsArrayOf(
    parseAsStringEnum(EncounterResolutionOutcomeSchema.values()),
  ).withDefault([]),
  lifecyclesFilter: parseAsArrayOf(
    parseAsStringEnum(EncounterLifecycleSchema.values()),
  ).withDefault([]),
  modifiersFilter: parseAsOperatorArrayOf(parseAsString).withDefault(
    createOperatorArrayDefault(),
  ),
  ownersFilter: parseAsArrayOf(
    parseAsStringEnum(EncounterOwnerSchema.values()),
  ).withDefault([]),
  patientsFilter: parseAsArrayOf(parseAsString).withDefault([]),
  payerIdentificationsFilter: parseAsOperatorArrayOf(parseAsString).withDefault(
    createOperatorArrayDefault(),
  ),
  payerNamePrefixesFilter: parseAsOperatorArrayOf(parseAsString).withDefault(
    createOperatorArrayDefault(),
  ),
  payersFilter: parseAsOperatorArrayOf(parseAsString).withDefault(
    createOperatorArrayDefault(),
  ),
  paymentUpdatesFilter: parseAsArrayOf(
    parseAsStringEnum(EncounterPaymentUpdateTypeSchema.values()),
  ).withDefault([]),
  practiceRequestStatusesFilter: parseAsOperatorArrayOf(
    parseAsStringEnum(PracticeRequestStatusSchema.values()),
  ).withDefault(createOperatorArrayDefault()),
  practiceRequestsFilter: parseAsOperatorArrayOf(parseAsString).withDefault(
    createOperatorArrayDefault(),
  ),
  procedureCodePrefixesFilter: parseAsOperatorArrayOf(
    parseAsString,
  ).withDefault(createOperatorArrayDefault()),
  procedureCodesFilter: parseAsOperatorArrayOf(parseAsString).withDefault(
    createOperatorArrayDefault(),
  ),
  projectsFilter: parseAsOperatorArrayOf(parseAsString).withDefault(
    createOperatorArrayDefault(),
  ),
  providersFilter: parseAsArrayOf(parseAsString).withDefault([]),
  query: parseAsString.withDefault(''),
  recoveredAmountMaxFilter: parseAsFloat,
  recoveredAmountMinFilter: parseAsFloat,
  remarkCodesFilter: parseAsOperatorArrayOf(parseAsString).withDefault(
    createOperatorArrayDefault(),
  ),
  remarkCodesFilterScope: parseAsStringEnum(
    PayerResponseFilterScopeSchema.values(),
  ).withDefault('any'),
  revenueCodesFilter: parseAsOperatorArrayOf(parseAsString).withDefault(
    createOperatorArrayDefault(),
  ),
  rootCauseThemesFilter: parseAsArrayOf(parseAsString).withDefault([]),
  serviceDateOlderThanDaysFilter: parseAsInteger,
  snoozedFilter: parseAsOperatorArrayOf(
    parseAsStringEnum(EncounterSnoozeFilterSchema.values()),
  ).withDefault(createOperatorArrayDefault()),
  sortKey: parseAsStringEnum(EncountersFiltersSortKeySchema.options),
  sortOrder: parseAsStringEnum(['asc', 'desc'] as const).withDefault('asc'),
  statusesFilter: parseAsOperatorArrayOf(
    parseAsStringEnum(EncounterEnrichmentStatusSchema.values()),
  ).withDefault(createOperatorArrayDefault()),
  tagsFilter: parseAsOperatorArrayOf(parseAsString).withDefault(
    createOperatorArrayDefault(),
  ),
  totalCollectedMaxFilter: parseAsFloat,
  totalCollectedMinFilter: parseAsFloat,
  totalOutstandingMaxFilter: parseAsFloat,
  totalOutstandingMinFilter: parseAsFloat,
  totalReceivableMaxFilter: parseAsFloat,
  totalReceivableMinFilter: parseAsFloat,
  workflowStatusesFilter: parseAsArrayOf(
    parseAsStringEnum(WorkflowStatusSchema.values()),
  ).withDefault([]),
  workflowStatusesSimplifiedFilter: parseAsArrayOf(
    parseAsStringEnum(WorkflowStatusSimplifiedSchema.values()),
  ).withDefault([]),
}

export const EncountersListQueryParams = {
  ...EncountersFiltersQueryParams,
  ...EncountersListPaginationQueryParams,
  ...encountersViewQueryParams,
  viewSelected: parseAsBoolean.withDefault(false),
}

export function useEncountersFilters() {
  const { setEncountersPagination } = useEncountersPagination()
  const [encounterFilters, _setEncounterFilters] = useQueryStates(
    EncountersFiltersQueryParams,
    { history: 'push' },
  )

  const setEncounterFilters: typeof _setEncounterFilters = (next, options) => {
    void setEncountersPagination((prev) => {
      return {
        ...prev,
        page: 1,
      }
    }, options)
    return _setEncounterFilters(next, options)
  }

  return {
    encounterFilters,
    setEncounterFilters,
  }
}

export const serializeEncountersFiltersQueryParams = createSerializer(
  EncountersFiltersQueryParams,
)

export type EncounterFiltersViewParams =
  components['schemas']['EncounterFiltersViewParams-Input']
export type TEncounterFilters = Values<typeof EncountersFiltersQueryParams>
export type SetEncounterFilters = SetValues<typeof EncountersFiltersQueryParams>
export type EncounterFiltersUpdate = Parameters<SetEncounterFilters>[0]

function resolveUpdatedFilterValue<Value>({
  current,
  fallback,
  update,
}: {
  current: Value
  fallback: Value
  update: Value | null | undefined
}) {
  return update === undefined ? current : (update ?? fallback)
}

export function applyEncounterFiltersUpdate({
  current,
  next,
}: {
  current: TEncounterFilters
  next: EncounterFiltersUpdate
}): TEncounterFilters {
  const update = typeof next === 'function' ? next(current) : next
  if (update === null) {
    return EmptyEncounterFilters
  }

  return {
    adjustmentCodesFilter: resolveUpdatedFilterValue({
      current: current.adjustmentCodesFilter,
      fallback: EmptyEncounterFilters.adjustmentCodesFilter,
      update: update.adjustmentCodesFilter,
    }),
    adjustmentCodesFilterScope: resolveUpdatedFilterValue({
      current: current.adjustmentCodesFilterScope,
      fallback: EmptyEncounterFilters.adjustmentCodesFilterScope,
      update: update.adjustmentCodesFilterScope,
    }),
    agentRunStatusesFilter: resolveUpdatedFilterValue({
      current: current.agentRunStatusesFilter,
      fallback: EmptyEncounterFilters.agentRunStatusesFilter,
      update: update.agentRunStatusesFilter,
    }),
    agingBucketsFilter: resolveUpdatedFilterValue({
      current: current.agingBucketsFilter,
      fallback: EmptyEncounterFilters.agingBucketsFilter,
      update: update.agingBucketsFilter,
    }),
    assignedBillerUsersFilter: resolveUpdatedFilterValue({
      current: current.assignedBillerUsersFilter,
      fallback: EmptyEncounterFilters.assignedBillerUsersFilter,
      update: update.assignedBillerUsersFilter,
    }),
    billingProviderNpisFilter: resolveUpdatedFilterValue({
      current: current.billingProviderNpisFilter,
      fallback: EmptyEncounterFilters.billingProviderNpisFilter,
      update: update.billingProviderNpisFilter,
    }),
    billingProviderTaxIdsFilter: resolveUpdatedFilterValue({
      current: current.billingProviderTaxIdsFilter,
      fallback: EmptyEncounterFilters.billingProviderTaxIdsFilter,
      update: update.billingProviderTaxIdsFilter,
    }),
    billingTypeFilter: resolveUpdatedFilterValue({
      current: current.billingTypeFilter,
      fallback: EmptyEncounterFilters.billingTypeFilter,
      update: update.billingTypeFilter,
    }),
    blockersFilter: resolveUpdatedFilterValue({
      current: current.blockersFilter,
      fallback: EmptyEncounterFilters.blockersFilter,
      update: update.blockersFilter,
    }),
    closeActionDateEndFilter: resolveUpdatedFilterValue({
      current: current.closeActionDateEndFilter,
      fallback: EmptyEncounterFilters.closeActionDateEndFilter,
      update: update.closeActionDateEndFilter,
    }),
    closeActionDatePresetFilter: resolveUpdatedFilterValue({
      current: current.closeActionDatePresetFilter,
      fallback: EmptyEncounterFilters.closeActionDatePresetFilter,
      update: update.closeActionDatePresetFilter,
    }),
    closeActionDateStartFilter: resolveUpdatedFilterValue({
      current: current.closeActionDateStartFilter,
      fallback: EmptyEncounterFilters.closeActionDateStartFilter,
      update: update.closeActionDateStartFilter,
    }),
    dateEndFilter: resolveUpdatedFilterValue({
      current: current.dateEndFilter,
      fallback: EmptyEncounterFilters.dateEndFilter,
      update: update.dateEndFilter,
    }),
    datePresetFilter: resolveUpdatedFilterValue({
      current: current.datePresetFilter,
      fallback: EmptyEncounterFilters.datePresetFilter,
      update: update.datePresetFilter,
    }),
    dateStartFilter: resolveUpdatedFilterValue({
      current: current.dateStartFilter,
      fallback: EmptyEncounterFilters.dateStartFilter,
      update: update.dateStartFilter,
    }),
    deniedProcedureCodesExactlyFilter: resolveUpdatedFilterValue({
      current: current.deniedProcedureCodesExactlyFilter,
      fallback: EmptyEncounterFilters.deniedProcedureCodesExactlyFilter,
      update: update.deniedProcedureCodesExactlyFilter,
    }),
    deniedProcedureCodesFilter: resolveUpdatedFilterValue({
      current: current.deniedProcedureCodesFilter,
      fallback: EmptyEncounterFilters.deniedProcedureCodesFilter,
      update: update.deniedProcedureCodesFilter,
    }),
    drilldownFilter: resolveUpdatedFilterValue({
      current: current.drilldownFilter,
      fallback: EmptyEncounterFilters.drilldownFilter,
      update: update.drilldownFilter,
    }),
    externalClaimIdsFilter: resolveUpdatedFilterValue({
      current: current.externalClaimIdsFilter,
      fallback: EmptyEncounterFilters.externalClaimIdsFilter,
      update: update.externalClaimIdsFilter,
    }),
    flaggedDateEndFilter: resolveUpdatedFilterValue({
      current: current.flaggedDateEndFilter,
      fallback: EmptyEncounterFilters.flaggedDateEndFilter,
      update: update.flaggedDateEndFilter,
    }),
    flaggedDatePresetFilter: resolveUpdatedFilterValue({
      current: current.flaggedDatePresetFilter,
      fallback: EmptyEncounterFilters.flaggedDatePresetFilter,
      update: update.flaggedDatePresetFilter,
    }),
    flaggedDateStartFilter: resolveUpdatedFilterValue({
      current: current.flaggedDateStartFilter,
      fallback: EmptyEncounterFilters.flaggedDateStartFilter,
      update: update.flaggedDateStartFilter,
    }),
    flaggedForJoyfulFollowupFilter: resolveUpdatedFilterValue({
      current: current.flaggedForJoyfulFollowupFilter,
      fallback: EmptyEncounterFilters.flaggedForJoyfulFollowupFilter,
      update: update.flaggedForJoyfulFollowupFilter,
    }),
    goLiveCohortFilter: resolveUpdatedFilterValue({
      current: current.goLiveCohortFilter,
      fallback: EmptyEncounterFilters.goLiveCohortFilter,
      update: update.goLiveCohortFilter,
    }),
    hasClaimResponsesFilter: resolveUpdatedFilterValue({
      current: current.hasClaimResponsesFilter,
      fallback: EmptyEncounterFilters.hasClaimResponsesFilter,
      update: update.hasClaimResponsesFilter,
    }),
    hasHistoricalDeniedClaimResponseFilter: resolveUpdatedFilterValue({
      current: current.hasHistoricalDeniedClaimResponseFilter,
      fallback: EmptyEncounterFilters.hasHistoricalDeniedClaimResponseFilter,
      update: update.hasHistoricalDeniedClaimResponseFilter,
    }),
    idsFilter: resolveUpdatedFilterValue({
      current: current.idsFilter,
      fallback: EmptyEncounterFilters.idsFilter,
      update: update.idsFilter,
    }),
    insuranceOutstandingMaxFilter: resolveUpdatedFilterValue({
      current: current.insuranceOutstandingMaxFilter,
      fallback: EmptyEncounterFilters.insuranceOutstandingMaxFilter,
      update: update.insuranceOutstandingMaxFilter,
    }),
    insuranceOutstandingMinFilter: resolveUpdatedFilterValue({
      current: current.insuranceOutstandingMinFilter,
      fallback: EmptyEncounterFilters.insuranceOutstandingMinFilter,
      update: update.insuranceOutstandingMinFilter,
    }),
    lastActionDateEndFilter: resolveUpdatedFilterValue({
      current: current.lastActionDateEndFilter,
      fallback: EmptyEncounterFilters.lastActionDateEndFilter,
      update: update.lastActionDateEndFilter,
    }),
    lastActionDatePresetFilter: resolveUpdatedFilterValue({
      current: current.lastActionDatePresetFilter,
      fallback: EmptyEncounterFilters.lastActionDatePresetFilter,
      update: update.lastActionDatePresetFilter,
    }),
    lastActionDateStartFilter: resolveUpdatedFilterValue({
      current: current.lastActionDateStartFilter,
      fallback: EmptyEncounterFilters.lastActionDateStartFilter,
      update: update.lastActionDateStartFilter,
    }),
    lastAgentRunDateEndFilter: resolveUpdatedFilterValue({
      current: current.lastAgentRunDateEndFilter,
      fallback: EmptyEncounterFilters.lastAgentRunDateEndFilter,
      update: update.lastAgentRunDateEndFilter,
    }),
    lastAgentRunDatePresetFilter: resolveUpdatedFilterValue({
      current: current.lastAgentRunDatePresetFilter,
      fallback: EmptyEncounterFilters.lastAgentRunDatePresetFilter,
      update: update.lastAgentRunDatePresetFilter,
    }),
    lastAgentRunDateStartFilter: resolveUpdatedFilterValue({
      current: current.lastAgentRunDateStartFilter,
      fallback: EmptyEncounterFilters.lastAgentRunDateStartFilter,
      update: update.lastAgentRunDateStartFilter,
    }),
    lastContactDateEndFilter: resolveUpdatedFilterValue({
      current: current.lastContactDateEndFilter,
      fallback: EmptyEncounterFilters.lastContactDateEndFilter,
      update: update.lastContactDateEndFilter,
    }),
    lastContactDatePresetFilter: resolveUpdatedFilterValue({
      current: current.lastContactDatePresetFilter,
      fallback: EmptyEncounterFilters.lastContactDatePresetFilter,
      update: update.lastContactDatePresetFilter,
    }),
    lastContactDateStartFilter: resolveUpdatedFilterValue({
      current: current.lastContactDateStartFilter,
      fallback: EmptyEncounterFilters.lastContactDateStartFilter,
      update: update.lastContactDateStartFilter,
    }),
    lastContactOlderThanDaysFilter: resolveUpdatedFilterValue({
      current: current.lastContactOlderThanDaysFilter,
      fallback: EmptyEncounterFilters.lastContactOlderThanDaysFilter,
      update: update.lastContactOlderThanDaysFilter,
    }),
    lastResubmissionAppealDateEndFilter: resolveUpdatedFilterValue({
      current: current.lastResubmissionAppealDateEndFilter,
      fallback: EmptyEncounterFilters.lastResubmissionAppealDateEndFilter,
      update: update.lastResubmissionAppealDateEndFilter,
    }),
    lastResubmissionAppealDatePresetFilter: resolveUpdatedFilterValue({
      current: current.lastResubmissionAppealDatePresetFilter,
      fallback: EmptyEncounterFilters.lastResubmissionAppealDatePresetFilter,
      update: update.lastResubmissionAppealDatePresetFilter,
    }),
    lastResubmissionAppealDateStartFilter: resolveUpdatedFilterValue({
      current: current.lastResubmissionAppealDateStartFilter,
      fallback: EmptyEncounterFilters.lastResubmissionAppealDateStartFilter,
      update: update.lastResubmissionAppealDateStartFilter,
    }),
    latestCandidStatusesFilter: resolveUpdatedFilterValue({
      current: current.latestCandidStatusesFilter,
      fallback: EmptyEncounterFilters.latestCandidStatusesFilter,
      update: update.latestCandidStatusesFilter,
    }),
    latestClaimPrimaryPayerIdentificationsFilter: resolveUpdatedFilterValue({
      current: current.latestClaimPrimaryPayerIdentificationsFilter,
      fallback:
        EmptyEncounterFilters.latestClaimPrimaryPayerIdentificationsFilter,
      update: update.latestClaimPrimaryPayerIdentificationsFilter,
    }),
    latestClaimPrimaryPayerNamesFilter: resolveUpdatedFilterValue({
      current: current.latestClaimPrimaryPayerNamesFilter,
      fallback: EmptyEncounterFilters.latestClaimPrimaryPayerNamesFilter,
      update: update.latestClaimPrimaryPayerNamesFilter,
    }),
    latestTebraStatusesFilter: resolveUpdatedFilterValue({
      current: current.latestTebraStatusesFilter,
      fallback: EmptyEncounterFilters.latestTebraStatusesFilter,
      update: update.latestTebraStatusesFilter,
    }),
    lifecycleOutcomesFilter: resolveUpdatedFilterValue({
      current: current.lifecycleOutcomesFilter,
      fallback: EmptyEncounterFilters.lifecycleOutcomesFilter,
      update: update.lifecycleOutcomesFilter,
    }),
    lifecyclesFilter: resolveUpdatedFilterValue({
      current: current.lifecyclesFilter,
      fallback: EmptyEncounterFilters.lifecyclesFilter,
      update: update.lifecyclesFilter,
    }),
    modifiersFilter: resolveUpdatedFilterValue({
      current: current.modifiersFilter,
      fallback: EmptyEncounterFilters.modifiersFilter,
      update: update.modifiersFilter,
    }),
    ownersFilter: resolveUpdatedFilterValue({
      current: current.ownersFilter,
      fallback: EmptyEncounterFilters.ownersFilter,
      update: update.ownersFilter,
    }),
    patientsFilter: resolveUpdatedFilterValue({
      current: current.patientsFilter,
      fallback: EmptyEncounterFilters.patientsFilter,
      update: update.patientsFilter,
    }),
    payerIdentificationsFilter: resolveUpdatedFilterValue({
      current: current.payerIdentificationsFilter,
      fallback: EmptyEncounterFilters.payerIdentificationsFilter,
      update: update.payerIdentificationsFilter,
    }),
    payerNamePrefixesFilter: resolveUpdatedFilterValue({
      current: current.payerNamePrefixesFilter,
      fallback: EmptyEncounterFilters.payerNamePrefixesFilter,
      update: update.payerNamePrefixesFilter,
    }),
    payersFilter: resolveUpdatedFilterValue({
      current: current.payersFilter,
      fallback: EmptyEncounterFilters.payersFilter,
      update: update.payersFilter,
    }),
    paymentUpdatesFilter: resolveUpdatedFilterValue({
      current: current.paymentUpdatesFilter,
      fallback: EmptyEncounterFilters.paymentUpdatesFilter,
      update: update.paymentUpdatesFilter,
    }),
    practiceRequestStatusesFilter: resolveUpdatedFilterValue({
      current: current.practiceRequestStatusesFilter,
      fallback: EmptyEncounterFilters.practiceRequestStatusesFilter,
      update: update.practiceRequestStatusesFilter,
    }),
    practiceRequestsFilter: resolveUpdatedFilterValue({
      current: current.practiceRequestsFilter,
      fallback: EmptyEncounterFilters.practiceRequestsFilter,
      update: update.practiceRequestsFilter,
    }),
    procedureCodePrefixesFilter: resolveUpdatedFilterValue({
      current: current.procedureCodePrefixesFilter,
      fallback: EmptyEncounterFilters.procedureCodePrefixesFilter,
      update: update.procedureCodePrefixesFilter,
    }),
    procedureCodesFilter: resolveUpdatedFilterValue({
      current: current.procedureCodesFilter,
      fallback: EmptyEncounterFilters.procedureCodesFilter,
      update: update.procedureCodesFilter,
    }),
    projectsFilter: resolveUpdatedFilterValue({
      current: current.projectsFilter,
      fallback: EmptyEncounterFilters.projectsFilter,
      update: update.projectsFilter,
    }),
    providersFilter: resolveUpdatedFilterValue({
      current: current.providersFilter,
      fallback: EmptyEncounterFilters.providersFilter,
      update: update.providersFilter,
    }),
    query: resolveUpdatedFilterValue({
      current: current.query,
      fallback: EmptyEncounterFilters.query,
      update: update.query,
    }),
    recoveredAmountMaxFilter: resolveUpdatedFilterValue({
      current: current.recoveredAmountMaxFilter,
      fallback: EmptyEncounterFilters.recoveredAmountMaxFilter,
      update: update.recoveredAmountMaxFilter,
    }),
    recoveredAmountMinFilter: resolveUpdatedFilterValue({
      current: current.recoveredAmountMinFilter,
      fallback: EmptyEncounterFilters.recoveredAmountMinFilter,
      update: update.recoveredAmountMinFilter,
    }),
    remarkCodesFilter: resolveUpdatedFilterValue({
      current: current.remarkCodesFilter,
      fallback: EmptyEncounterFilters.remarkCodesFilter,
      update: update.remarkCodesFilter,
    }),
    remarkCodesFilterScope: resolveUpdatedFilterValue({
      current: current.remarkCodesFilterScope,
      fallback: EmptyEncounterFilters.remarkCodesFilterScope,
      update: update.remarkCodesFilterScope,
    }),
    revenueCodesFilter: resolveUpdatedFilterValue({
      current: current.revenueCodesFilter,
      fallback: EmptyEncounterFilters.revenueCodesFilter,
      update: update.revenueCodesFilter,
    }),
    rootCauseThemesFilter: resolveUpdatedFilterValue({
      current: current.rootCauseThemesFilter,
      fallback: EmptyEncounterFilters.rootCauseThemesFilter,
      update: update.rootCauseThemesFilter,
    }),
    serviceDateOlderThanDaysFilter: resolveUpdatedFilterValue({
      current: current.serviceDateOlderThanDaysFilter,
      fallback: EmptyEncounterFilters.serviceDateOlderThanDaysFilter,
      update: update.serviceDateOlderThanDaysFilter,
    }),
    snoozedFilter: resolveUpdatedFilterValue({
      current: current.snoozedFilter,
      fallback: EmptyEncounterFilters.snoozedFilter,
      update: update.snoozedFilter,
    }),
    sortKey: resolveUpdatedFilterValue({
      current: current.sortKey,
      fallback: EmptyEncounterFilters.sortKey,
      update: update.sortKey,
    }),
    sortOrder: resolveUpdatedFilterValue({
      current: current.sortOrder,
      fallback: EmptyEncounterFilters.sortOrder,
      update: update.sortOrder,
    }),
    statusesFilter: resolveUpdatedFilterValue({
      current: current.statusesFilter,
      fallback: EmptyEncounterFilters.statusesFilter,
      update: update.statusesFilter,
    }),
    tagsFilter: resolveUpdatedFilterValue({
      current: current.tagsFilter,
      fallback: EmptyEncounterFilters.tagsFilter,
      update: update.tagsFilter,
    }),
    totalCollectedMaxFilter: resolveUpdatedFilterValue({
      current: current.totalCollectedMaxFilter,
      fallback: EmptyEncounterFilters.totalCollectedMaxFilter,
      update: update.totalCollectedMaxFilter,
    }),
    totalCollectedMinFilter: resolveUpdatedFilterValue({
      current: current.totalCollectedMinFilter,
      fallback: EmptyEncounterFilters.totalCollectedMinFilter,
      update: update.totalCollectedMinFilter,
    }),
    totalOutstandingMaxFilter: resolveUpdatedFilterValue({
      current: current.totalOutstandingMaxFilter,
      fallback: EmptyEncounterFilters.totalOutstandingMaxFilter,
      update: update.totalOutstandingMaxFilter,
    }),
    totalOutstandingMinFilter: resolveUpdatedFilterValue({
      current: current.totalOutstandingMinFilter,
      fallback: EmptyEncounterFilters.totalOutstandingMinFilter,
      update: update.totalOutstandingMinFilter,
    }),
    totalReceivableMaxFilter: resolveUpdatedFilterValue({
      current: current.totalReceivableMaxFilter,
      fallback: EmptyEncounterFilters.totalReceivableMaxFilter,
      update: update.totalReceivableMaxFilter,
    }),
    totalReceivableMinFilter: resolveUpdatedFilterValue({
      current: current.totalReceivableMinFilter,
      fallback: EmptyEncounterFilters.totalReceivableMinFilter,
      update: update.totalReceivableMinFilter,
    }),
    workflowStatusesFilter: resolveUpdatedFilterValue({
      current: current.workflowStatusesFilter,
      fallback: EmptyEncounterFilters.workflowStatusesFilter,
      update: update.workflowStatusesFilter,
    }),
    workflowStatusesSimplifiedFilter: resolveUpdatedFilterValue({
      current: current.workflowStatusesSimplifiedFilter,
      fallback: EmptyEncounterFilters.workflowStatusesSimplifiedFilter,
      update: update.workflowStatusesSimplifiedFilter,
    }),
  }
}

function normalizeNumberFilter(
  value: number | string | null | undefined,
): number | null {
  if (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '')
  ) {
    return null
  }

  const parsed = typeof value === 'string' ? Number(value.trim()) : value
  return Number.isFinite(parsed) ? parsed : null
}

// Match backend defaults in convert_api_filters_to_search (payers/projects/practice requests → ANY).
const DEFAULT_PAYERS_FILTER_OPERATOR: ArrayFilterOperator = 'any'
const DEFAULT_PROJECTS_FILTER_OPERATOR: ArrayFilterOperator = 'any'
const DEFAULT_PRACTICE_REQUESTS_FILTER_OPERATOR: ArrayFilterOperator = 'any'
const DEFAULT_STATUSES_FILTER_OPERATOR: ArrayFilterOperator = 'any'

export function normalizeEncounterFilters(
  filters: Partial<EncounterFiltersViewParams> | null | undefined,
): TEncounterFilters {
  return {
    ...EmptyEncounterFilters,
    adjustmentCodesFilter: {
      operator: filters?.adjustmentCodesFilterOperator ?? 'all',
      values: filters?.adjustmentCodesFilter ?? [],
    },
    adjustmentCodesFilterScope: filters?.adjustmentCodesFilterScope ?? 'any',
    agentRunStatusesFilter: filters?.agentRunStatusesFilter ?? [],
    agingBucketsFilter: filters?.agingBucketsFilter ?? [],
    assignedBillerUsersFilter: filters?.assignedBillerUsersFilter ?? [],
    billingProviderNpisFilter: {
      operator: filters?.billingProviderNpisFilterOperator ?? 'all',
      values: filters?.billingProviderNpisFilter ?? [],
    },
    billingProviderTaxIdsFilter: {
      operator: filters?.billingProviderTaxIdsFilterOperator ?? 'all',
      values: filters?.billingProviderTaxIdsFilter ?? [],
    },
    billingTypeFilter: filters?.billingTypeFilter ?? null,
    blockersFilter: filters?.blockersFilter ?? [],
    closeActionDateEndFilter: toPlainDate(
      filters?.closeActionDateEndFilter ?? null,
    ),
    closeActionDateStartFilter: toPlainDate(
      filters?.closeActionDateStartFilter ?? null,
    ),
    dateEndFilter: toPlainDate(filters?.dateEndFilter ?? null),
    dateStartFilter: toPlainDate(filters?.dateStartFilter ?? null),
    deniedProcedureCodesExactlyFilter:
      filters?.deniedProcedureCodesExactlyFilter ?? [],
    deniedProcedureCodesFilter: {
      operator: filters?.deniedProcedureCodesFilterOperator ?? 'all',
      values: filters?.deniedProcedureCodesFilter ?? [],
    },
    drilldownFilter: filters?.drilldownFilter ?? null,
    externalClaimIdsFilter: filters?.externalClaimIdsFilter ?? [],
    flaggedDateEndFilter: toPlainDate(filters?.flaggedDateEndFilter ?? null),
    flaggedDateStartFilter: toPlainDate(
      filters?.flaggedDateStartFilter ?? null,
    ),
    flaggedForJoyfulFollowupFilter:
      filters?.flaggedForJoyfulFollowupFilter ?? null,
    goLiveCohortFilter: filters?.goLiveCohortFilter ?? null,
    hasClaimResponsesFilter: filters?.hasClaimResponsesFilter ?? null,
    hasHistoricalDeniedClaimResponseFilter:
      filters?.hasHistoricalDeniedClaimResponseFilter ?? null,
    idsFilter: filters?.idsFilter ?? [],
    insuranceOutstandingMaxFilter: normalizeNumberFilter(
      filters?.insuranceOutstandingMaxFilter,
    ),
    insuranceOutstandingMinFilter: normalizeNumberFilter(
      filters?.insuranceOutstandingMinFilter,
    ),
    lastActionDateEndFilter: toPlainDate(
      filters?.lastActionDateEndFilter ?? null,
    ),
    lastActionDateStartFilter: toPlainDate(
      filters?.lastActionDateStartFilter ?? null,
    ),
    lastAgentRunDateEndFilter: toPlainDate(
      filters?.lastAgentRunDateEndFilter ?? null,
    ),
    lastAgentRunDateStartFilter: toPlainDate(
      filters?.lastAgentRunDateStartFilter ?? null,
    ),
    lastContactDateEndFilter: toPlainDate(
      filters?.lastContactDateEndFilter ?? null,
    ),
    lastContactDateStartFilter: toPlainDate(
      filters?.lastContactDateStartFilter ?? null,
    ),
    lastContactOlderThanDaysFilter:
      filters?.lastContactOlderThanDaysFilter ?? null,
    lastResubmissionAppealDateEndFilter: toPlainDate(
      filters?.lastResubmissionAppealDateEndFilter ?? null,
    ),
    lastResubmissionAppealDateStartFilter: toPlainDate(
      filters?.lastResubmissionAppealDateStartFilter ?? null,
    ),
    latestCandidStatusesFilter: {
      operator: filters?.latestCandidStatusesFilterOperator ?? 'all',
      values: filters?.latestCandidStatusesFilter ?? [],
    },
    latestClaimPrimaryPayerIdentificationsFilter: {
      operator:
        filters?.latestClaimPrimaryPayerIdentificationsFilterOperator ?? 'all',
      values: filters?.latestClaimPrimaryPayerIdentificationsFilter ?? [],
    },
    latestClaimPrimaryPayerNamesFilter: {
      operator: filters?.latestClaimPrimaryPayerNamesFilterOperator ?? 'all',
      values: filters?.latestClaimPrimaryPayerNamesFilter ?? [],
    },
    latestTebraStatusesFilter: {
      operator: filters?.latestTebraStatusesFilterOperator ?? 'all',
      values: filters?.latestTebraStatusesFilter ?? [],
    },
    lifecycleOutcomesFilter: filters?.lifecycleOutcomesFilter ?? [],
    lifecyclesFilter: filters?.lifecyclesFilter ?? [],
    modifiersFilter: {
      operator: filters?.modifiersFilterOperator ?? 'all',
      values: filters?.modifiersFilter ?? [],
    },
    ownersFilter: filters?.ownersFilter ?? [],
    patientsFilter: filters?.patientsFilter ?? [],
    payerIdentificationsFilter: createOperatorArrayDefault(),
    payerNamePrefixesFilter: createOperatorArrayDefault(),
    payersFilter: {
      operator:
        filters?.payersFilterOperator ??
        filters?.payerIdentificationsFilterOperator ??
        filters?.payerNamePrefixesFilterOperator ??
        DEFAULT_PAYERS_FILTER_OPERATOR,
      values: appendUniqueValues(
        appendUniqueValues(
          filters?.payersFilter ?? [],
          (filters?.payerIdentificationsFilter ?? []).map(
            createPayerIdentificationFilterValue,
          ),
        ),
        (filters?.payerNamePrefixesFilter ?? []).map(
          createPayerNamePrefixFilterValue,
        ),
      ),
    },
    paymentUpdatesFilter: filters?.paymentUpdatesFilter ?? [],
    practiceRequestStatusesFilter: {
      operator: resolveAnyOrNoneFilterRequestOperator(
        filters?.practiceRequestStatusesFilterOperator,
      ),
      values: filters?.practiceRequestStatusesFilter ?? [],
    },
    practiceRequestsFilter: {
      operator:
        filters?.practiceRequestsFilterOperator ??
        DEFAULT_PRACTICE_REQUESTS_FILTER_OPERATOR,
      values: filters?.practiceRequestsFilter ?? [],
    },
    procedureCodePrefixesFilter: createOperatorArrayDefault(),
    procedureCodesFilter: {
      operator: filters?.procedureCodesFilterOperator ?? 'all',
      values: appendUniqueValues(
        filters?.procedureCodesFilter ?? [],
        (filters?.procedureCodePrefixesFilter ?? []).map(
          createProcedureCodePrefixFilterValue,
        ),
      ),
    },
    projectsFilter: {
      operator:
        filters?.projectsFilterOperator ?? DEFAULT_PROJECTS_FILTER_OPERATOR,
      values: filters?.projectsFilter ?? [],
    },
    providersFilter: filters?.providersFilter ?? [],
    query: filters?.query ?? '',
    recoveredAmountMaxFilter: normalizeNumberFilter(
      filters?.recoveredAmountMaxFilter,
    ),
    recoveredAmountMinFilter: normalizeNumberFilter(
      filters?.recoveredAmountMinFilter,
    ),
    remarkCodesFilter: {
      operator: filters?.remarkCodesFilterOperator ?? 'all',
      values: filters?.remarkCodesFilter ?? [],
    },
    remarkCodesFilterScope: filters?.remarkCodesFilterScope ?? 'any',
    revenueCodesFilter: {
      operator: filters?.revenueCodesFilterOperator ?? 'all',
      values: filters?.revenueCodesFilter ?? [],
    },
    rootCauseThemesFilter: filters?.rootCauseThemesFilter ?? [],
    serviceDateOlderThanDaysFilter:
      filters?.serviceDateOlderThanDaysFilter ?? null,
    snoozedFilter: normalizeSnoozedOperatorArrayFilter(filters),
    statusesFilter: {
      operator:
        filters?.statusesFilterOperator ?? DEFAULT_STATUSES_FILTER_OPERATOR,
      values: filters?.statusesFilter ?? [],
    },
    tagsFilter: {
      operator: filters?.tagsFilterOperator ?? 'all',
      values: filters?.tagsFilter ?? [],
    },
    totalCollectedMaxFilter: normalizeNumberFilter(
      filters?.totalCollectedMaxFilter,
    ),
    totalCollectedMinFilter: normalizeNumberFilter(
      filters?.totalCollectedMinFilter,
    ),
    totalOutstandingMaxFilter: normalizeNumberFilter(
      filters?.totalOutstandingMaxFilter,
    ),
    totalOutstandingMinFilter: normalizeNumberFilter(
      filters?.totalOutstandingMinFilter,
    ),
    totalReceivableMaxFilter: normalizeNumberFilter(
      filters?.totalReceivableMaxFilter,
    ),
    totalReceivableMinFilter: normalizeNumberFilter(
      filters?.totalReceivableMinFilter,
    ),
    workflowStatusesFilter: filters?.workflowStatusesFilter ?? [],
    workflowStatusesSimplifiedFilter:
      filters?.workflowStatusesSimplifiedFilter ?? [],
  }
}

interface EncounterListQueryVariables extends Omit<
  NonNullable<operations['listEncounters']['parameters']['query']>,
  'limit' | 'page' | 'sortKey' | 'sortOrder' | 'view'
> {
  goLiveCohortFilter?: components['schemas']['GoLiveCohort'] | null
}

export const EmptyEncounterFilters: TEncounterFilters = {
  adjustmentCodesFilter: createOperatorArrayDefault(),
  adjustmentCodesFilterScope: 'any',
  agentRunStatusesFilter: [],
  agingBucketsFilter: [],
  assignedBillerUsersFilter: [],
  billingProviderNpisFilter: createOperatorArrayDefault(),
  billingProviderTaxIdsFilter: createOperatorArrayDefault(),
  billingTypeFilter: null,
  blockersFilter: [],
  closeActionDateEndFilter: null,
  closeActionDatePresetFilter: null,
  closeActionDateStartFilter: null,
  dateEndFilter: null,
  datePresetFilter: null,
  dateStartFilter: null,
  deniedProcedureCodesExactlyFilter: [],
  deniedProcedureCodesFilter: createOperatorArrayDefault(),
  drilldownFilter: null,
  externalClaimIdsFilter: [],
  flaggedDateEndFilter: null,
  flaggedDatePresetFilter: null,
  flaggedDateStartFilter: null,
  flaggedForJoyfulFollowupFilter: null,
  goLiveCohortFilter: null,
  hasClaimResponsesFilter: null,
  hasHistoricalDeniedClaimResponseFilter: null,
  idsFilter: [],
  insuranceOutstandingMaxFilter: null,
  insuranceOutstandingMinFilter: null,
  lastActionDateEndFilter: null,
  lastActionDatePresetFilter: null,
  lastActionDateStartFilter: null,
  lastAgentRunDateEndFilter: null,
  lastAgentRunDatePresetFilter: null,
  lastAgentRunDateStartFilter: null,
  lastContactDateEndFilter: null,
  lastContactDatePresetFilter: null,
  lastContactDateStartFilter: null,
  lastContactOlderThanDaysFilter: null,
  lastResubmissionAppealDateEndFilter: null,
  lastResubmissionAppealDatePresetFilter: null,
  lastResubmissionAppealDateStartFilter: null,
  latestCandidStatusesFilter: createOperatorArrayDefault(),
  latestClaimPrimaryPayerIdentificationsFilter: createOperatorArrayDefault(),
  latestClaimPrimaryPayerNamesFilter: createOperatorArrayDefault(),
  latestTebraStatusesFilter: createOperatorArrayDefault(),
  lifecycleOutcomesFilter: [],
  lifecyclesFilter: [],
  modifiersFilter: createOperatorArrayDefault(),
  ownersFilter: [],
  patientsFilter: [],
  payerIdentificationsFilter: createOperatorArrayDefault(),
  payerNamePrefixesFilter: createOperatorArrayDefault(),
  payersFilter: createOperatorArrayDefault(),
  paymentUpdatesFilter: [],
  practiceRequestStatusesFilter: createOperatorArrayDefault(),
  practiceRequestsFilter: createOperatorArrayDefault(),
  procedureCodePrefixesFilter: createOperatorArrayDefault(),
  procedureCodesFilter: createOperatorArrayDefault(),
  projectsFilter: createOperatorArrayDefault(),
  providersFilter: [],
  query: '',
  recoveredAmountMaxFilter: null,
  recoveredAmountMinFilter: null,
  remarkCodesFilter: createOperatorArrayDefault(),
  remarkCodesFilterScope: 'any',
  revenueCodesFilter: createOperatorArrayDefault(),
  rootCauseThemesFilter: [],
  serviceDateOlderThanDaysFilter: null,
  snoozedFilter: createOperatorArrayDefault(),
  sortKey: null,
  sortOrder: 'asc',
  statusesFilter: createOperatorArrayDefault(),
  tagsFilter: createOperatorArrayDefault(),
  totalCollectedMaxFilter: null,
  totalCollectedMinFilter: null,
  totalOutstandingMaxFilter: null,
  totalOutstandingMinFilter: null,
  totalReceivableMaxFilter: null,
  totalReceivableMinFilter: null,
  workflowStatusesFilter: [],
  workflowStatusesSimplifiedFilter: [],
}

export const EmptyEncountersListParams = {
  ...EmptyEncounterFilters,
  limit: 50,
  page: 1,
  view: 'all',
  viewSelected: false,
} satisfies Values<typeof EncountersListQueryParams>

function resolveAnyOrNoneFilterRequestOperator(
  operator: ArrayFilterOperator | null | undefined,
): 'any' | 'none' {
  if (operator === 'none' || operator === 'notAll') {
    return 'none'
  }

  return 'any'
}

function normalizeSnoozedOperatorArrayFilter(
  filters?: {
    snoozedFilter?: unknown
    snoozedFilterOperator?: ArrayFilterOperator | null
  } | null,
): OperatorArrayFilter<components['schemas']['EncounterSnoozeFilter']> {
  const raw = filters?.snoozedFilter
  const candidates = Array.isArray(raw) ? raw : [raw]
  const values = candidates.flatMap((value) => {
    const parsed = EncounterSnoozeFilterSchema.schema.safeParse(value)
    return parsed.success ? [parsed.data] : []
  })
  const operator = resolveAnyOrNoneFilterRequestOperator(
    filters?.snoozedFilterOperator,
  )

  return {
    operator,
    values,
  }
}

function toFilterAmount(
  value: string | number | null | undefined,
): number | null {
  if (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '')
  ) {
    return null
  }

  return Number(value)
}

/**
 * Inverse of {@link buildEncounterFiltersPayload}: hydrate a stored
 * `EncounterFiltersViewParams` payload (e.g. a saved filter or automation)
 * back into the in-memory `TEncounterFilters` shape used by the encounters
 * table. Date presets and sort are not persisted in the payload, so they reset
 * to their empty defaults (dates are already resolved to concrete start/end
 * values at save time).
 */
export function normalizeEncounterFiltersViewParams(
  filters: EncounterFiltersViewParams,
): TEncounterFilters {
  return {
    ...EmptyEncounterFilters,
    adjustmentCodesFilter: {
      operator: filters.adjustmentCodesFilterOperator ?? 'all',
      values: filters.adjustmentCodesFilter ?? [],
    },
    adjustmentCodesFilterScope: filters.adjustmentCodesFilterScope ?? 'any',
    agentRunStatusesFilter: filters.agentRunStatusesFilter ?? [],
    agingBucketsFilter: filters.agingBucketsFilter ?? [],
    assignedBillerUsersFilter: filters.assignedBillerUsersFilter ?? [],
    billingProviderNpisFilter: {
      operator: filters.billingProviderNpisFilterOperator ?? 'all',
      values: filters.billingProviderNpisFilter ?? [],
    },
    billingProviderTaxIdsFilter: {
      operator: filters.billingProviderTaxIdsFilterOperator ?? 'all',
      values: filters.billingProviderTaxIdsFilter ?? [],
    },
    billingTypeFilter: filters.billingTypeFilter ?? null,
    blockersFilter: filters.blockersFilter ?? [],
    closeActionDateEndFilter: toPlainDate(
      filters.closeActionDateEndFilter ?? null,
    ),
    closeActionDateStartFilter: toPlainDate(
      filters.closeActionDateStartFilter ?? null,
    ),
    dateEndFilter: toPlainDate(filters.dateEndFilter ?? null),
    dateStartFilter: toPlainDate(filters.dateStartFilter ?? null),
    deniedProcedureCodesExactlyFilter:
      filters.deniedProcedureCodesExactlyFilter ?? [],
    deniedProcedureCodesFilter: {
      operator: filters.deniedProcedureCodesFilterOperator ?? 'all',
      values: filters.deniedProcedureCodesFilter ?? [],
    },
    drilldownFilter: filters.drilldownFilter ?? null,
    externalClaimIdsFilter: filters.externalClaimIdsFilter ?? [],
    flaggedDateEndFilter: toPlainDate(filters.flaggedDateEndFilter ?? null),
    flaggedDateStartFilter: toPlainDate(filters.flaggedDateStartFilter ?? null),
    flaggedForJoyfulFollowupFilter:
      filters.flaggedForJoyfulFollowupFilter ?? null,
    goLiveCohortFilter: filters.goLiveCohortFilter ?? null,
    hasClaimResponsesFilter: filters.hasClaimResponsesFilter ?? null,
    hasHistoricalDeniedClaimResponseFilter:
      filters.hasHistoricalDeniedClaimResponseFilter ?? null,
    idsFilter: filters.idsFilter ?? [],
    insuranceOutstandingMaxFilter: toFilterAmount(
      filters.insuranceOutstandingMaxFilter,
    ),
    insuranceOutstandingMinFilter: toFilterAmount(
      filters.insuranceOutstandingMinFilter,
    ),
    lastActionDateEndFilter: toPlainDate(
      filters.lastActionDateEndFilter ?? null,
    ),
    lastActionDateStartFilter: toPlainDate(
      filters.lastActionDateStartFilter ?? null,
    ),
    lastAgentRunDateEndFilter: toPlainDate(
      filters.lastAgentRunDateEndFilter ?? null,
    ),
    lastAgentRunDateStartFilter: toPlainDate(
      filters.lastAgentRunDateStartFilter ?? null,
    ),
    lastContactDateEndFilter: toPlainDate(
      filters.lastContactDateEndFilter ?? null,
    ),
    lastContactDateStartFilter: toPlainDate(
      filters.lastContactDateStartFilter ?? null,
    ),
    lastContactOlderThanDaysFilter:
      filters.lastContactOlderThanDaysFilter ?? null,
    lastResubmissionAppealDateEndFilter: toPlainDate(
      filters.lastResubmissionAppealDateEndFilter ?? null,
    ),
    lastResubmissionAppealDateStartFilter: toPlainDate(
      filters.lastResubmissionAppealDateStartFilter ?? null,
    ),
    latestCandidStatusesFilter: {
      operator: filters.latestCandidStatusesFilterOperator ?? 'all',
      values: filters.latestCandidStatusesFilter ?? [],
    },
    latestClaimPrimaryPayerIdentificationsFilter: {
      operator:
        filters.latestClaimPrimaryPayerIdentificationsFilterOperator ?? 'all',
      values: filters.latestClaimPrimaryPayerIdentificationsFilter ?? [],
    },
    latestClaimPrimaryPayerNamesFilter: {
      operator: filters.latestClaimPrimaryPayerNamesFilterOperator ?? 'all',
      values: filters.latestClaimPrimaryPayerNamesFilter ?? [],
    },
    latestTebraStatusesFilter: {
      operator: filters.latestTebraStatusesFilterOperator ?? 'all',
      values: filters.latestTebraStatusesFilter ?? [],
    },
    lifecycleOutcomesFilter: filters.lifecycleOutcomesFilter ?? [],
    lifecyclesFilter: filters.lifecyclesFilter ?? [],
    modifiersFilter: {
      operator: filters.modifiersFilterOperator ?? 'all',
      values: filters.modifiersFilter ?? [],
    },
    ownersFilter: filters.ownersFilter ?? [],
    patientsFilter: filters.patientsFilter ?? [],
    payerIdentificationsFilter: createOperatorArrayDefault(),
    payerNamePrefixesFilter: createOperatorArrayDefault(),
    payersFilter: {
      operator:
        filters.payersFilterOperator ??
        filters.payerIdentificationsFilterOperator ??
        filters.payerNamePrefixesFilterOperator ??
        DEFAULT_PAYERS_FILTER_OPERATOR,
      values: appendUniqueValues(
        appendUniqueValues(
          filters.payersFilter ?? [],
          (filters.payerIdentificationsFilter ?? []).map(
            createPayerIdentificationFilterValue,
          ),
        ),
        (filters.payerNamePrefixesFilter ?? []).map(
          createPayerNamePrefixFilterValue,
        ),
      ),
    },
    paymentUpdatesFilter: filters.paymentUpdatesFilter ?? [],
    practiceRequestStatusesFilter: {
      operator: resolveAnyOrNoneFilterRequestOperator(
        filters.practiceRequestStatusesFilterOperator,
      ),
      values: filters.practiceRequestStatusesFilter ?? [],
    },
    practiceRequestsFilter: {
      operator:
        filters.practiceRequestsFilterOperator ??
        DEFAULT_PRACTICE_REQUESTS_FILTER_OPERATOR,
      values: filters.practiceRequestsFilter ?? [],
    },
    procedureCodePrefixesFilter: createOperatorArrayDefault(),
    procedureCodesFilter: {
      operator: filters.procedureCodesFilterOperator ?? 'all',
      values: appendUniqueValues(
        filters.procedureCodesFilter ?? [],
        (filters.procedureCodePrefixesFilter ?? []).map(
          createProcedureCodePrefixFilterValue,
        ),
      ),
    },
    projectsFilter: {
      operator:
        filters.projectsFilterOperator ?? DEFAULT_PROJECTS_FILTER_OPERATOR,
      values: filters.projectsFilter ?? [],
    },
    providersFilter: filters.providersFilter ?? [],
    query: filters.query ?? '',
    recoveredAmountMaxFilter: toFilterAmount(filters.recoveredAmountMaxFilter),
    recoveredAmountMinFilter: toFilterAmount(filters.recoveredAmountMinFilter),
    remarkCodesFilter: {
      operator: filters.remarkCodesFilterOperator ?? 'all',
      values: filters.remarkCodesFilter ?? [],
    },
    remarkCodesFilterScope: filters.remarkCodesFilterScope ?? 'any',
    revenueCodesFilter: {
      operator: filters.revenueCodesFilterOperator ?? 'all',
      values: filters.revenueCodesFilter ?? [],
    },
    rootCauseThemesFilter: filters.rootCauseThemesFilter ?? [],
    serviceDateOlderThanDaysFilter:
      filters.serviceDateOlderThanDaysFilter ?? null,
    snoozedFilter: normalizeSnoozedOperatorArrayFilter(filters),
    statusesFilter: {
      operator:
        filters.statusesFilterOperator ?? DEFAULT_STATUSES_FILTER_OPERATOR,
      values: filters.statusesFilter ?? [],
    },
    tagsFilter: {
      operator: filters.tagsFilterOperator ?? 'all',
      values: filters.tagsFilter ?? [],
    },
    totalCollectedMaxFilter: toFilterAmount(filters.totalCollectedMaxFilter),
    totalCollectedMinFilter: toFilterAmount(filters.totalCollectedMinFilter),
    totalOutstandingMaxFilter: toFilterAmount(
      filters.totalOutstandingMaxFilter,
    ),
    totalOutstandingMinFilter: toFilterAmount(
      filters.totalOutstandingMinFilter,
    ),
    totalReceivableMaxFilter: toFilterAmount(filters.totalReceivableMaxFilter),
    totalReceivableMinFilter: toFilterAmount(filters.totalReceivableMinFilter),
    workflowStatusesFilter: filters.workflowStatusesFilter ?? [],
    workflowStatusesSimplifiedFilter:
      filters.workflowStatusesSimplifiedFilter ?? [],
  }
}

interface DateFilterRangeParams {
  endDate: Temporal.PlainDate | null
  presetId: CalendarPresetId | null
  startDate: Temporal.PlainDate | null
}

function resolveDateFilterRange({
  endDate,
  presetId,
  startDate,
}: DateFilterRangeParams) {
  if (presetId) {
    return resolveCalendarPresetDateRange({ presetId })
  }

  return {
    from: startDate,
    to: endDate,
  }
}

function serializePlainDate(date: Temporal.PlainDate | null) {
  return date ? date.toString() : null
}

function countDateFilter({
  endDate,
  presetId,
  startDate,
}: DateFilterRangeParams) {
  if (presetId) {
    return 2
  }

  return Number(startDate !== null) + Number(endDate !== null)
}

function buildEncounterDateFilterQueryVariables(
  encounterFilters: TEncounterFilters,
) {
  const closeActionDateRange = resolveDateFilterRange({
    endDate: encounterFilters.closeActionDateEndFilter,
    presetId: encounterFilters.closeActionDatePresetFilter,
    startDate: encounterFilters.closeActionDateStartFilter,
  })
  const dateRange = resolveDateFilterRange({
    endDate: encounterFilters.dateEndFilter,
    presetId: encounterFilters.datePresetFilter,
    startDate: encounterFilters.dateStartFilter,
  })
  const lastActionDateRange = resolveDateFilterRange({
    endDate: encounterFilters.lastActionDateEndFilter,
    presetId: encounterFilters.lastActionDatePresetFilter,
    startDate: encounterFilters.lastActionDateStartFilter,
  })
  const lastAgentRunDateRange = resolveDateFilterRange({
    endDate: encounterFilters.lastAgentRunDateEndFilter,
    presetId: encounterFilters.lastAgentRunDatePresetFilter,
    startDate: encounterFilters.lastAgentRunDateStartFilter,
  })
  const lastContactDateRange = resolveDateFilterRange({
    endDate: encounterFilters.lastContactDateEndFilter,
    presetId: encounterFilters.lastContactDatePresetFilter,
    startDate: encounterFilters.lastContactDateStartFilter,
  })
  const flaggedDateRange = resolveDateFilterRange({
    endDate: encounterFilters.flaggedDateEndFilter,
    presetId: encounterFilters.flaggedDatePresetFilter,
    startDate: encounterFilters.flaggedDateStartFilter,
  })
  const lastResubmissionAppealDateRange = resolveDateFilterRange({
    endDate: encounterFilters.lastResubmissionAppealDateEndFilter,
    presetId: encounterFilters.lastResubmissionAppealDatePresetFilter,
    startDate: encounterFilters.lastResubmissionAppealDateStartFilter,
  })

  return {
    closeActionDateEndFilter: serializePlainDate(closeActionDateRange.to),
    closeActionDateStartFilter: serializePlainDate(closeActionDateRange.from),
    dateEndFilter: serializePlainDate(dateRange.to),
    dateStartFilter: serializePlainDate(dateRange.from),
    flaggedDateEndFilter: serializePlainDate(flaggedDateRange.to),
    flaggedDateStartFilter: serializePlainDate(flaggedDateRange.from),
    lastActionDateEndFilter: serializePlainDate(lastActionDateRange.to),
    lastActionDateStartFilter: serializePlainDate(lastActionDateRange.from),
    lastAgentRunDateEndFilter: serializePlainDate(lastAgentRunDateRange.to),
    lastAgentRunDateStartFilter: serializePlainDate(lastAgentRunDateRange.from),
    lastContactDateEndFilter: serializePlainDate(lastContactDateRange.to),
    lastContactDateStartFilter: serializePlainDate(lastContactDateRange.from),
    lastResubmissionAppealDateEndFilter: serializePlainDate(
      lastResubmissionAppealDateRange.to,
    ),
    lastResubmissionAppealDateStartFilter: serializePlainDate(
      lastResubmissionAppealDateRange.from,
    ),
  }
}

export function countEncounterFilters(filters: TEncounterFilters) {
  return (
    countDateFilter({
      endDate: filters.dateEndFilter,
      presetId: filters.datePresetFilter,
      startDate: filters.dateStartFilter,
    }) +
    countDateFilter({
      endDate: filters.lastActionDateEndFilter,
      presetId: filters.lastActionDatePresetFilter,
      startDate: filters.lastActionDateStartFilter,
    }) +
    countDateFilter({
      endDate: filters.flaggedDateEndFilter,
      presetId: filters.flaggedDatePresetFilter,
      startDate: filters.flaggedDateStartFilter,
    }) +
    countDateFilter({
      endDate: filters.lastResubmissionAppealDateEndFilter,
      presetId: filters.lastResubmissionAppealDatePresetFilter,
      startDate: filters.lastResubmissionAppealDateStartFilter,
    }) +
    countDateFilter({
      endDate: filters.lastContactDateEndFilter,
      presetId: filters.lastContactDatePresetFilter,
      startDate: filters.lastContactDateStartFilter,
    }) +
    countDateFilter({
      endDate: filters.lastAgentRunDateEndFilter,
      presetId: filters.lastAgentRunDatePresetFilter,
      startDate: filters.lastAgentRunDateStartFilter,
    }) +
    Number(filters.billingTypeFilter !== null) +
    Number(filters.goLiveCohortFilter !== null) +
    filters.idsFilter.length +
    filters.externalClaimIdsFilter.length +
    countDateFilter({
      endDate: filters.closeActionDateEndFilter,
      presetId: filters.closeActionDatePresetFilter,
      startDate: filters.closeActionDateStartFilter,
    }) +
    filters.payersFilter.values.length +
    filters.latestClaimPrimaryPayerNamesFilter.values.length +
    filters.latestClaimPrimaryPayerIdentificationsFilter.values.length +
    filters.latestCandidStatusesFilter.values.length +
    filters.latestTebraStatusesFilter.values.length +
    filters.providersFilter.length +
    filters.patientsFilter.length +
    filters.statusesFilter.values.length +
    filters.adjustmentCodesFilter.values.length +
    filters.procedureCodesFilter.values.length +
    filters.deniedProcedureCodesFilter.values.length +
    filters.deniedProcedureCodesExactlyFilter.length +
    filters.modifiersFilter.values.length +
    filters.remarkCodesFilter.values.length +
    filters.revenueCodesFilter.values.length +
    filters.lifecycleOutcomesFilter.length +
    filters.lifecyclesFilter.length +
    filters.ownersFilter.length +
    filters.blockersFilter.length +
    filters.workflowStatusesFilter.length +
    filters.workflowStatusesSimplifiedFilter.length +
    filters.assignedBillerUsersFilter.length +
    filters.agingBucketsFilter.length +
    filters.agentRunStatusesFilter.length +
    filters.tagsFilter.values.length +
    filters.projectsFilter.values.length +
    filters.practiceRequestsFilter.values.length +
    filters.practiceRequestStatusesFilter.values.length +
    (filters.flaggedForJoyfulFollowupFilter === null ? 0 : 1) +
    (filters.hasClaimResponsesFilter === null ? 0 : 1) +
    (filters.hasHistoricalDeniedClaimResponseFilter === null ? 0 : 1) +
    filters.snoozedFilter.values.length +
    (filters.query ? 1 : 0) +
    filters.billingProviderNpisFilter.values.length +
    filters.billingProviderTaxIdsFilter.values.length +
    filters.paymentUpdatesFilter.length +
    filters.rootCauseThemesFilter.length +
    Number(filters.lastContactOlderThanDaysFilter !== null) +
    Number(filters.serviceDateOlderThanDaysFilter !== null) +
    Number(filters.drilldownFilter !== null) +
    Number(filters.recoveredAmountMinFilter !== null) +
    Number(filters.recoveredAmountMaxFilter !== null) +
    Number(filters.insuranceOutstandingMinFilter !== null) +
    Number(filters.insuranceOutstandingMaxFilter !== null) +
    Number(filters.totalCollectedMinFilter !== null) +
    Number(filters.totalCollectedMaxFilter !== null) +
    Number(filters.totalOutstandingMinFilter !== null) +
    Number(filters.totalOutstandingMaxFilter !== null) +
    Number(filters.totalReceivableMinFilter !== null) +
    Number(filters.totalReceivableMaxFilter !== null)
  )
}

export function buildEncounterFilterQueryVariables(
  encounterFilters: TEncounterFilters,
): EncounterListQueryVariables {
  const dateFilterQueryVariables =
    buildEncounterDateFilterQueryVariables(encounterFilters)
  const payerNameFilterValues = getPayerNameFilterValues(
    encounterFilters.payersFilter.values,
  )
  const payerIdentificationFilterValues = appendUniqueValues(
    encounterFilters.payerIdentificationsFilter.values,
    getPayerIdentificationFilterValues(encounterFilters.payersFilter.values),
  )
  const payerNamePrefixFilterValues = appendUniqueValues(
    encounterFilters.payerNamePrefixesFilter.values,
    getPayerNamePrefixFilterValues(encounterFilters.payersFilter.values),
  )
  const procedureCodeFilterValues = getProcedureCodeFilterValues(
    encounterFilters.procedureCodesFilter.values,
  )
  const procedureCodePrefixFilterValues = appendUniqueValues(
    encounterFilters.procedureCodePrefixesFilter.values,
    getProcedureCodePrefixFilterValues(
      encounterFilters.procedureCodesFilter.values,
    ),
  )

  return {
    adjustmentCodesFilter: encounterFilters.adjustmentCodesFilter.values,
    adjustmentCodesFilterOperator:
      encounterFilters.adjustmentCodesFilter.operator,
    adjustmentCodesFilterScope: encounterFilters.adjustmentCodesFilterScope,
    agentRunStatusesFilter: encounterFilters.agentRunStatusesFilter,
    agingBucketsFilter: encounterFilters.agingBucketsFilter,
    assignedBillerUsersFilter: encounterFilters.assignedBillerUsersFilter,
    billingProviderNpisFilter:
      encounterFilters.billingProviderNpisFilter.values,
    billingProviderNpisFilterOperator:
      encounterFilters.billingProviderNpisFilter.operator,
    billingProviderTaxIdsFilter:
      encounterFilters.billingProviderTaxIdsFilter.values,
    billingProviderTaxIdsFilterOperator:
      encounterFilters.billingProviderTaxIdsFilter.operator,
    billingTypeFilter: encounterFilters.billingTypeFilter,
    blockersFilter: encounterFilters.blockersFilter,
    ...dateFilterQueryVariables,
    deniedProcedureCodesExactlyFilter:
      encounterFilters.deniedProcedureCodesExactlyFilter,
    deniedProcedureCodesFilter:
      encounterFilters.deniedProcedureCodesFilter.values,
    deniedProcedureCodesFilterOperator:
      encounterFilters.deniedProcedureCodesFilter.operator,
    drilldownFilter: encounterFilters.drilldownFilter,
    externalClaimIdsFilter: encounterFilters.externalClaimIdsFilter,
    flaggedForJoyfulFollowupFilter:
      encounterFilters.flaggedForJoyfulFollowupFilter,
    goLiveCohortFilter: encounterFilters.goLiveCohortFilter,
    hasClaimResponsesFilter: encounterFilters.hasClaimResponsesFilter,
    hasHistoricalDeniedClaimResponseFilter:
      encounterFilters.hasHistoricalDeniedClaimResponseFilter,
    idsFilter: encounterFilters.idsFilter,
    insuranceOutstandingMaxFilter:
      encounterFilters.insuranceOutstandingMaxFilter,
    insuranceOutstandingMinFilter:
      encounterFilters.insuranceOutstandingMinFilter,
    lastContactOlderThanDaysFilter:
      encounterFilters.lastContactOlderThanDaysFilter,
    latestCandidStatusesFilter:
      encounterFilters.latestCandidStatusesFilter.values,
    latestCandidStatusesFilterOperator:
      encounterFilters.latestCandidStatusesFilter.operator,
    latestClaimPrimaryPayerIdentificationsFilter:
      encounterFilters.latestClaimPrimaryPayerIdentificationsFilter.values,
    latestClaimPrimaryPayerIdentificationsFilterOperator:
      encounterFilters.latestClaimPrimaryPayerIdentificationsFilter.operator,
    latestClaimPrimaryPayerNamesFilter:
      encounterFilters.latestClaimPrimaryPayerNamesFilter.values,
    latestClaimPrimaryPayerNamesFilterOperator:
      encounterFilters.latestClaimPrimaryPayerNamesFilter.operator,
    latestTebraStatusesFilter:
      encounterFilters.latestTebraStatusesFilter.values,
    latestTebraStatusesFilterOperator:
      encounterFilters.latestTebraStatusesFilter.operator,
    lifecycleOutcomesFilter: encounterFilters.lifecycleOutcomesFilter,
    lifecyclesFilter: encounterFilters.lifecyclesFilter,
    modifiersFilter: encounterFilters.modifiersFilter.values,
    modifiersFilterOperator: encounterFilters.modifiersFilter.operator,
    ownersFilter: encounterFilters.ownersFilter,
    patientsFilter: encounterFilters.patientsFilter,
    payerIdentificationsFilter: payerIdentificationFilterValues,
    payerIdentificationsFilterOperator:
      payerIdentificationFilterValues.length > 0
        ? encounterFilters.payersFilter.operator
        : encounterFilters.payerIdentificationsFilter.operator,
    payerNamePrefixesFilter: payerNamePrefixFilterValues,
    payerNamePrefixesFilterOperator:
      payerNamePrefixFilterValues.length > 0
        ? encounterFilters.payersFilter.operator
        : encounterFilters.payerNamePrefixesFilter.operator,
    payersFilter: payerNameFilterValues,
    payersFilterOperator: encounterFilters.payersFilter.operator,
    paymentUpdatesFilter: encounterFilters.paymentUpdatesFilter,
    practiceRequestStatusesFilter:
      encounterFilters.practiceRequestStatusesFilter.values,
    practiceRequestStatusesFilterOperator:
      resolveAnyOrNoneFilterRequestOperator(
        encounterFilters.practiceRequestStatusesFilter.operator,
      ),
    practiceRequestsFilter: encounterFilters.practiceRequestsFilter.values,
    practiceRequestsFilterOperator:
      encounterFilters.practiceRequestsFilter.operator,
    procedureCodePrefixesFilter: procedureCodePrefixFilterValues,
    procedureCodePrefixesFilterOperator:
      procedureCodePrefixFilterValues.length > 0
        ? encounterFilters.procedureCodesFilter.operator
        : encounterFilters.procedureCodePrefixesFilter.operator,
    procedureCodesFilter: procedureCodeFilterValues,
    procedureCodesFilterOperator:
      encounterFilters.procedureCodesFilter.operator,
    projectsFilter: encounterFilters.projectsFilter.values,
    projectsFilterOperator: encounterFilters.projectsFilter.operator,
    providersFilter: encounterFilters.providersFilter,
    query: encounterFilters.query,
    recoveredAmountMaxFilter: encounterFilters.recoveredAmountMaxFilter,
    recoveredAmountMinFilter: encounterFilters.recoveredAmountMinFilter,
    remarkCodesFilter: encounterFilters.remarkCodesFilter.values,
    remarkCodesFilterOperator: encounterFilters.remarkCodesFilter.operator,
    remarkCodesFilterScope: encounterFilters.remarkCodesFilterScope,
    revenueCodesFilter: encounterFilters.revenueCodesFilter.values,
    revenueCodesFilterOperator: encounterFilters.revenueCodesFilter.operator,
    rootCauseThemesFilter: encounterFilters.rootCauseThemesFilter,
    serviceDateOlderThanDaysFilter:
      encounterFilters.serviceDateOlderThanDaysFilter,
    snoozedFilter: encounterFilters.snoozedFilter.values,
    snoozedFilterOperator: resolveAnyOrNoneFilterRequestOperator(
      encounterFilters.snoozedFilter.operator,
    ),
    statusesFilter: encounterFilters.statusesFilter.values,
    statusesFilterOperator: resolveAnyOrNoneFilterRequestOperator(
      encounterFilters.statusesFilter.operator,
    ),
    tagsFilter: encounterFilters.tagsFilter.values,
    tagsFilterOperator: encounterFilters.tagsFilter.operator,
    totalCollectedMaxFilter: encounterFilters.totalCollectedMaxFilter,
    totalCollectedMinFilter: encounterFilters.totalCollectedMinFilter,
    totalOutstandingMaxFilter: encounterFilters.totalOutstandingMaxFilter,
    totalOutstandingMinFilter: encounterFilters.totalOutstandingMinFilter,
    totalReceivableMaxFilter: encounterFilters.totalReceivableMaxFilter,
    totalReceivableMinFilter: encounterFilters.totalReceivableMinFilter,
    workflowStatusesFilter: encounterFilters.workflowStatusesFilter,
    workflowStatusesSimplifiedFilter:
      encounterFilters.workflowStatusesSimplifiedFilter,
  }
}

export function buildEncounterFiltersPayload({
  encounterFilters,
  encountersView,
}: {
  encounterFilters: TEncounterFilters
  encountersView: components['schemas']['EncounterListView']
}): EncounterFiltersViewParams {
  const {
    closeActionDatePresetFilter: _closeActionDatePresetFilter,
    datePresetFilter: _datePresetFilter,
    flaggedDatePresetFilter: _flaggedDatePresetFilter,
    lastActionDatePresetFilter: _lastActionDatePresetFilter,
    lastAgentRunDatePresetFilter: _lastAgentRunDatePresetFilter,
    lastContactDatePresetFilter: _lastContactDatePresetFilter,
    lastResubmissionAppealDatePresetFilter:
      _lastResubmissionAppealDatePresetFilter,
    sortKey: _sortKey,
    sortOrder: _sortOrder,
    ...payloadFilters
  } = encounterFilters
  const dateFilterQueryVariables =
    buildEncounterDateFilterQueryVariables(encounterFilters)
  const payerNameFilterValues = getPayerNameFilterValues(
    encounterFilters.payersFilter.values,
  )
  const payerIdentificationFilterValues = appendUniqueValues(
    encounterFilters.payerIdentificationsFilter.values,
    getPayerIdentificationFilterValues(encounterFilters.payersFilter.values),
  )
  const payerNamePrefixFilterValues = appendUniqueValues(
    encounterFilters.payerNamePrefixesFilter.values,
    getPayerNamePrefixFilterValues(encounterFilters.payersFilter.values),
  )
  const procedureCodeFilterValues = getProcedureCodeFilterValues(
    encounterFilters.procedureCodesFilter.values,
  )
  const procedureCodePrefixFilterValues = appendUniqueValues(
    encounterFilters.procedureCodePrefixesFilter.values,
    getProcedureCodePrefixFilterValues(
      encounterFilters.procedureCodesFilter.values,
    ),
  )

  return {
    ...payloadFilters,
    adjustmentCodesFilter: encounterFilters.adjustmentCodesFilter.values,
    adjustmentCodesFilterOperator:
      encounterFilters.adjustmentCodesFilter.operator,
    adjustmentCodesFilterScope: encounterFilters.adjustmentCodesFilterScope,
    billingProviderNpisFilter:
      encounterFilters.billingProviderNpisFilter.values,
    billingProviderNpisFilterOperator:
      encounterFilters.billingProviderNpisFilter.operator,
    billingProviderTaxIdsFilter:
      encounterFilters.billingProviderTaxIdsFilter.values,
    billingProviderTaxIdsFilterOperator:
      encounterFilters.billingProviderTaxIdsFilter.operator,
    ...dateFilterQueryVariables,
    deniedProcedureCodesFilter:
      encounterFilters.deniedProcedureCodesFilter.values,
    deniedProcedureCodesFilterOperator:
      encounterFilters.deniedProcedureCodesFilter.operator,
    latestCandidStatusesFilter:
      encounterFilters.latestCandidStatusesFilter.values,
    latestCandidStatusesFilterOperator:
      encounterFilters.latestCandidStatusesFilter.operator,
    latestClaimPrimaryPayerIdentificationsFilter:
      encounterFilters.latestClaimPrimaryPayerIdentificationsFilter.values,
    latestClaimPrimaryPayerIdentificationsFilterOperator:
      encounterFilters.latestClaimPrimaryPayerIdentificationsFilter.operator,
    latestClaimPrimaryPayerNamesFilter:
      encounterFilters.latestClaimPrimaryPayerNamesFilter.values,
    latestClaimPrimaryPayerNamesFilterOperator:
      encounterFilters.latestClaimPrimaryPayerNamesFilter.operator,
    latestTebraStatusesFilter:
      encounterFilters.latestTebraStatusesFilter.values,
    latestTebraStatusesFilterOperator:
      encounterFilters.latestTebraStatusesFilter.operator,
    modifiersFilter: encounterFilters.modifiersFilter.values,
    modifiersFilterOperator: encounterFilters.modifiersFilter.operator,
    payerIdentificationsFilter: payerIdentificationFilterValues,
    payerIdentificationsFilterOperator:
      payerIdentificationFilterValues.length > 0
        ? encounterFilters.payersFilter.operator
        : encounterFilters.payerIdentificationsFilter.operator,
    payerNamePrefixesFilter: payerNamePrefixFilterValues,
    payerNamePrefixesFilterOperator:
      payerNamePrefixFilterValues.length > 0
        ? encounterFilters.payersFilter.operator
        : encounterFilters.payerNamePrefixesFilter.operator,
    payersFilter: payerNameFilterValues,
    payersFilterOperator: encounterFilters.payersFilter.operator,
    practiceRequestStatusesFilter:
      encounterFilters.practiceRequestStatusesFilter.values,
    practiceRequestStatusesFilterOperator:
      resolveAnyOrNoneFilterRequestOperator(
        encounterFilters.practiceRequestStatusesFilter.operator,
      ),
    practiceRequestsFilter: encounterFilters.practiceRequestsFilter.values,
    practiceRequestsFilterOperator:
      encounterFilters.practiceRequestsFilter.operator,
    procedureCodePrefixesFilter: procedureCodePrefixFilterValues,
    procedureCodePrefixesFilterOperator:
      procedureCodePrefixFilterValues.length > 0
        ? encounterFilters.procedureCodesFilter.operator
        : encounterFilters.procedureCodePrefixesFilter.operator,
    procedureCodesFilter: procedureCodeFilterValues,
    procedureCodesFilterOperator:
      encounterFilters.procedureCodesFilter.operator,
    projectsFilter: encounterFilters.projectsFilter.values,
    projectsFilterOperator: encounterFilters.projectsFilter.operator,
    remarkCodesFilter: encounterFilters.remarkCodesFilter.values,
    remarkCodesFilterOperator: encounterFilters.remarkCodesFilter.operator,
    remarkCodesFilterScope: encounterFilters.remarkCodesFilterScope,
    revenueCodesFilter: encounterFilters.revenueCodesFilter.values,
    revenueCodesFilterOperator: encounterFilters.revenueCodesFilter.operator,
    snoozedFilter: encounterFilters.snoozedFilter.values,
    snoozedFilterOperator: resolveAnyOrNoneFilterRequestOperator(
      encounterFilters.snoozedFilter.operator,
    ),
    statusesFilter: encounterFilters.statusesFilter.values,
    statusesFilterOperator: resolveAnyOrNoneFilterRequestOperator(
      encounterFilters.statusesFilter.operator,
    ),
    tagsFilter: encounterFilters.tagsFilter.values,
    tagsFilterOperator: encounterFilters.tagsFilter.operator,
    view: encountersView,
  }
}

/**
 * Fetch paginated encounter items for the selected view.
 */
export function useEncounterItemsQuery({
  defaultFilters,
  projectedEncounterFilters,
}: {
  defaultFilters?: Partial<
    operations['listEncounterItems']['parameters']['query']
  >
  projectedEncounterFilters?: TEncounterFilters
} = {}) {
  const orgSlug = useApiOrgSlug()
  const { encountersPagination } = useEncountersPagination()
  const { encounterFilters } = useEncountersFilters()
  const variables = buildEncounterFilterQueryVariables(
    projectedEncounterFilters ?? encounterFilters,
  )
  const [view] = useEncountersView()
  return useApiQuery('get', '/{org_slug}/encounters/items', {
    params: {
      path: {
        org_slug: orgSlug,
      },
      query: {
        ...variables,
        limit: encountersPagination.limit,
        page: encountersPagination.page,
        sort_key: encounterFilters.sortKey ?? undefined,
        sort_order: encounterFilters.sortOrder,
        view,
        ...defaultFilters,
      },
    },
  })
}

/**
 * Fetch total and per-view counts for the current filters, without the selected view.
 */
export function useEncounterCountsQuery({
  defaultFilters,
  projectedEncounterFilters,
}: {
  defaultFilters?: Partial<
    operations['getEncounterCounts']['parameters']['query']
  >
  projectedEncounterFilters?: TEncounterFilters
} = {}) {
  const orgSlug = useApiOrgSlug()
  const { encounterFilters } = useEncountersFilters()
  const variables = buildEncounterFilterQueryVariables(
    projectedEncounterFilters ?? encounterFilters,
  )
  return useApiQuery(
    'get',
    '/{org_slug}/encounters/counts',
    {
      params: {
        path: {
          org_slug: orgSlug,
        },
        query: {
          ...variables,
          ...defaultFilters,
        },
      },
    },
    {
      placeholderData: keepPreviousData,
    },
  )
}

/**
 * Hook to fetch encounter facets.
 * Facets are loaded once per account and don't change based on filters/search.
 * This allows facets to be cached and reused across filter/page changes.
 */
export function useEncounterFacetsQuery() {
  const orgSlug = useApiOrgSlug()
  return useApiQuery(
    'get',
    '/{org_slug}/encounters/facets',
    {
      params: {
        path: {
          org_slug: orgSlug,
        },
        // No filter params - facets are always for the whole account
        query: {},
      },
    },
    {
      // Facets don't change often, so we can cache them longer
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  )
}

export function createEncounterSortUpdater<
  TFilters extends Pick<TEncounterFilters, 'sortKey' | 'sortOrder'>,
>(sorting: Updater<SortingState>): Updater<TFilters> {
  return (prev) => {
    const [columnSort] =
      typeof sorting === 'function'
        ? sorting(
            prev.sortKey === null
              ? []
              : [{ desc: prev.sortOrder === 'desc', id: prev.sortKey }],
          )
        : sorting
    return {
      ...prev,
      sortKey:
        columnSort === undefined
          ? null
          : EncountersFiltersSortKeySchema.parse(columnSort.id),
      sortOrder: columnSort?.desc ? 'desc' : 'asc',
    }
  }
}

export function buildEncounterTableSortState<
  TFilters extends Pick<TEncounterFilters, 'sortKey' | 'sortOrder'>,
>(filters: TFilters): SortingState {
  return filters.sortKey === null
    ? []
    : [{ desc: filters.sortOrder === 'desc', id: filters.sortKey }]
}

/**
 * Type alias for the items query return type.
 */
export type EncounterItemsQueryResult = ReturnType<
  typeof useEncounterItemsQuery
>

/**
 * Type alias for the counts query return type.
 */
export type EncounterCountsQueryResult = ReturnType<
  typeof useEncounterCountsQuery
>

/**
 * Type alias for the facets query return type.
 */
export type EncounterFacetsQueryResult = ReturnType<
  typeof useEncounterFacetsQuery
>
